/**
 * FoundingSuccessPage — Stripe success redirect target for the standalone
 * $79.99 Founder checkout. Polls GET /api/founder/checkout-standalone/status
 * until the position is promoted from RESERVED to ASSIGNED, then shows the
 * confetti "you're Founding Business #NN" state.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 1800;
const MAX_POLL_MS = 45_000;

export default function FoundingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | active | failed | recon
  const [result, setResult] = useState(null);
  const started = useRef(Date.now());

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(
          `${API}/founder/checkout-standalone/status/${sessionId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (data.status === "active") {
          setResult(data);
          setState("active");
          return;
        }
        if (data.status === "failed") {
          setState("failed");
          return;
        }
        if (data.status === "requires_reconciliation") {
          setState("recon");
          return;
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Couldn't verify checkout");
      }
      if (Date.now() - started.current > MAX_POLL_MS) {
        setState("failed");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const number = result?.founding_member_number;
  const padded = useMemo(() => (number ? String(number).padStart(3, "0") : null), [number]);

  return (
    <div className="min-h-screen bg-tbc-cream" data-testid="founding-success-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="rounded-3xl bg-tbc-navy text-white p-10 sm:p-14 text-center shadow-xl">
          {state === "polling" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-tbc-gold" />
              <h1 className="mt-6 font-heading text-3xl">Confirming your Founding position…</h1>
              <p className="mt-3 text-white/70 text-sm">Stripe is settling the charge. This usually takes a couple of seconds.</p>
            </>
          )}
          {state === "active" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-tbc-gold/15 border border-tbc-gold/40 flex items-center justify-center">
                <Crown className="w-8 h-8 text-tbc-gold" />
              </div>
              <p className="mt-6 text-[11px] uppercase tracking-[0.28em] text-tbc-gold font-semibold">Welcome to the Founding 100</p>
              <h1 className="mt-3 font-heading text-white text-5xl sm:text-6xl leading-none">
                Founding Business <span className="text-tbc-gold">#{padded}</span>
              </h1>
              <p className="mt-6 text-white/85 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                Your position is permanent, your badge is live, and your lifetime benefits are unlocked. Thank you for being part of TBC&apos;s founding class.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate(`/founding-100#${padded}`)}
                  className="h-12 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
                  data-testid="founding-success-wall-btn"
                >
                  See my place on the wall
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="h-12 rounded-full border-white/25 text-white bg-transparent hover:bg-white hover:text-tbc-navy"
                >
                  Manage business
                </Button>
              </div>
            </>
          )}
          {state === "failed" && (
            <>
              <h1 className="font-heading text-3xl">We couldn&apos;t confirm this checkout</h1>
              <p className="mt-3 text-white/70 text-sm">Your Founding position hasn&apos;t been assigned. If you were charged, our team will reconcile it manually — please contact support with your session ID.</p>
              {sessionId && (
                <p className="mt-2 text-xs text-white/50 font-mono">Session: {sessionId.slice(-12)}</p>
              )}
              <div className="mt-8">
                <Link to="/founding-business">
                  <Button className="h-11 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90">
                    Back to Founding Business
                  </Button>
                </Link>
              </div>
            </>
          )}
          {state === "recon" && (
            <>
              <h1 className="font-heading text-3xl">Almost there — needs a manual reconcile</h1>
              <p className="mt-3 text-white/70 text-sm">
                Your payment went through, but a position couldn&apos;t be automatically assigned (usually because the Founding 100 filled between button-tap and settlement). Our team will place you or refund you within one business day.
              </p>
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/contact"><Button className="h-11 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90">Contact support</Button></Link>
              </div>
            </>
          )}
        </div>
        {state === "active" && (
          <p className="mt-6 text-center text-xs text-tbc-navy/50 inline-flex items-center gap-1.5 justify-center w-full">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Founder benefits already active on your profile.
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
}
